import React, { RefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { Button } from '@xo-union/tk-component-buttons';
import classNames from 'classnames';
import styles from './styles.scss';
import { AIFieldProps, useAiTextGenerationContext } from '../../context/AiTextGenerationContext';
import { TrackingContext } from '../../TrackingContext';
import AiFloatingMenu from './Subcomponents/AiFloatingMenu/AiFloatingMenu';
import AiCustomPromptInput from './Subcomponents/AiCustomPromptInput/AiCustomPromptInput';
import { customPromptPresetOption } from '../../services/wws-api/wws-api.service';
import AiButtonTooltip from '../Tooltips/AiButtonTooltip/AiButtonTooltip';
import { FieldInputProps } from 'formik';

export const buildField = (
  field: FieldInputProps<string>,
  setFieldText: AIFieldProps['setFieldText'],
  relatedFields?: Record<string, unknown>
): AIFieldProps => {
  const filteredRelatedFields: Record<string, any> = {};

  if (relatedFields) {
    Object.keys(relatedFields).forEach(key => {
      if (key !== field.name && typeof relatedFields[key] === 'string') {
        filteredRelatedFields[key] = relatedFields[key];
      }
    });
  }

  return {
    fieldName: field.name,
    fieldText: field.value,
    setFieldText,
    relatedFields: filteredRelatedFields,
  };
};

type OpenElement = 'menu' | 'custom-prompt-input' | null;

export interface AiButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  menuDirection?: 'up' | 'down';
  field?: AIFieldProps;
  isCustomPromptInputSingleLine?: boolean;
  isParentFocused?: boolean;
  shouldRenderCustomPromptInput?: boolean;
  defaultOpenElement?: OpenElement;
}

export const AiButton: React.FC<AiButtonProps> = ({
  className,
  onClick,
  menuDirection = 'down',
  isParentFocused = false,
  field,
  isCustomPromptInputSingleLine = false,
  shouldRenderCustomPromptInput = false,
  defaultOpenElement = null,
  ...rest
}) => {
  const { track } = useContext(TrackingContext);
  const {
    presetOptions,
    modalOpen,
    setModalOpen,
    setSelectedOption,
    setField,
    generatedText,
  } = useAiTextGenerationContext();

  const filteredOptions = useMemo(() => {
    const hasFieldText = Boolean(field?.fieldText?.trim());
    const hasGeneratedText = Boolean(generatedText?.trim());

    return presetOptions.filter(option => {
      if (option.availableWhen === 'always') return true;
      if (option.availableWhen === 'when-empty') return !hasFieldText && !hasGeneratedText;
      if (option.availableWhen === 'when-has-content') return hasFieldText || hasGeneratedText;
      return false;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetOptions, field?.fieldText, generatedText]);

  const [openElement, setOpenElement] = React.useState<OpenElement>(defaultOpenElement);
  const isMenuOpen = openElement === 'menu';
  const isCustomPromptInputOpen = openElement === 'custom-prompt-input';

  const [shouldDisableTooltip, setShouldDisableTooltip] = React.useState(false);

  const onAiButtonClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShouldDisableTooltip(true);

    if (openElement === null) {
      track('AI Button Clicked', {
        action: 'WWS AI',
        actionName: 'icon clicked',
      });

      setOpenElement('menu');
    }

    if (isCustomPromptInputOpen) {
      setOpenElement('menu');
    }

    if (isMenuOpen) {
      setOpenElement(null);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const onTextGenOptionSelected = (optionValue: string) => {
    setOpenElement(null);
    setSelectedOption(optionValue);

    if (!modalOpen) {
      setModalOpen(true);
    }
  };

  const onFloatMenuOptionSelect = (optionValue: string) => {
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'prompt selected',
      prompt_content: optionValue,
    });
    if (!modalOpen) {
      setField(field);
    }
    setOpenElement(null);

    if (optionValue === customPromptPresetOption.value && shouldRenderCustomPromptInput) {
      setOpenElement('custom-prompt-input');
      return;
    }

    onTextGenOptionSelected(optionValue);
  };

  const instanceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutsideOfAIButton = (
      event: MouseEvent | KeyboardEvent,
      activeInstanceRef?: RefObject<HTMLDivElement | null>
    ) => {
      if (activeInstanceRef?.current && !activeInstanceRef.current.contains(event.target as Node)) {
        if (isMenuOpen || isCustomPromptInputOpen) {
          setOpenElement(null);
        }
      }
    };

    if (isMenuOpen || isCustomPromptInputOpen) {
      document.addEventListener('mousedown', (event: MouseEvent) => onClickOutsideOfAIButton(event, instanceRef));
      document.addEventListener('keydown', (event: KeyboardEvent) => onClickOutsideOfAIButton(event, instanceRef));
    } else {
      document.removeEventListener('mousedown', onClickOutsideOfAIButton);
      document.removeEventListener('keydown', onClickOutsideOfAIButton);
    }

    return () => {
      document.removeEventListener('mousedown', onClickOutsideOfAIButton);
      document.removeEventListener('keydown', onClickOutsideOfAIButton);
    };
  }, [isMenuOpen, isCustomPromptInputOpen, instanceRef]);

  return (
    <div className={styles.aiButton__TooltipWrapper}>
      <div
        className={classNames(styles.aiButton__Wrapper, className, {
          [styles.aiButton__Wrapper__inputIsOpen]: isCustomPromptInputOpen,
          [styles.aiButton__Wrapper__inputIsClosed]: !isCustomPromptInputOpen,
        })}
        ref={instanceRef}
        data-testid="AiButton__wrapper"
      >
        <AiCustomPromptInput
          isOpen={isCustomPromptInputOpen}
          onSubmit={onTextGenOptionSelected}
          isSingleLine={isCustomPromptInputSingleLine}
        >
          <div className={styles.aiButton__floatingMenuWrapper}>
            <Button
              className={classNames(styles.aiButton, {
                [styles.hidden]: !isParentFocused && openElement === null,
              })}
              color="secondary-alternative"
              size="sm"
              aria-label="Ai Assist"
              type="button"
              {...rest}
              onClick={onAiButtonClicked}
              role="button"
              data-testid="ai-button"
            >
              <svg
                className={styles.aiIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M8.97363 2.6084C10.5932 6.03849 13.429 8.77053 16.915 10.2773L17.6182 10.5811L17.251 10.7178C13.511 12.2314 10.5113 15.1422 8.87988 18.8223L8.79883 19L8.53613 18.3594C7.08373 14.9163 4.44304 12.1115 1.09766 10.4541L1 10.3926L1.43848 10.1807C4.6173 8.67902 7.19159 6.14746 8.73242 3.01758L8.97363 2.6084ZM15.918 1C16.5251 2.28548 17.588 3.30937 18.8945 3.87402L19.1582 3.98828L19.0205 4.03906C17.6187 4.60649 16.4942 5.69774 15.8828 7.07715L15.8525 7.14355L15.7539 6.90332C15.2094 5.61295 14.2197 4.56153 12.9658 3.94043L12.9297 3.91699L13.0938 3.83789C14.285 3.27498 15.2497 2.32631 15.8271 1.15332L15.918 1Z"
                  fill="white"
                />
              </svg>
            </Button>
            <AiFloatingMenu
              presetOptions={filteredOptions}
              onOptionSelect={onFloatMenuOptionSelect}
              isMenuOpen={isMenuOpen}
              menuDirection={menuDirection}
            />
          </div>
        </AiCustomPromptInput>
      </div>
      <AiButtonTooltip shouldDisableTooltip={shouldDisableTooltip} isButtonActive={isParentFocused} />
    </div>
  );
};
